/** The whole decision logic of the world budget. Pure: no Three.js, no DOM. */
import type { ModuleSpec } from './registry';

/** A level per module id (the full `Composition`, or any subset of modules the specs cover). */
type Levels = Record<string, string>;

const byPriority = (specs: readonly ModuleSpec[]) => [...specs].sort((a, b) => a.priority - b.priority);
const levelIndex = (m: ModuleSpec, level: string) => m.levels.indexOf(level);
/** A module missing from the map counts as its minLevel. */
const levelOf = (m: ModuleSpec, c: Levels): string => c[m.id] ?? m.minLevel;

/** GPU memory (MB) of a composition plus the core. */
export function memoryOf(specs: readonly ModuleSpec[], c: Levels, coreMb: number): number {
  return specs.reduce((sum, m) => sum + (m.memMb[levelOf(m, c)] ?? 0), coreMb);
}

/** Starts from `cap`, then lowers levels in priority order (lowest priority first) until core + modules fit. */
export function composeByMemory<C extends Levels>(specs: readonly ModuleSpec[], budgetMb: number, cap: C, coreMb: number): C {
  let c: C = { ...cap };
  while (memoryOf(specs, c, coreMb) > budgetMb) {
    const next = stepDown(specs, c);
    if (!next) break;
    c = next;
  }
  return c;
}

/** The lowest-priority module that is above its minLevel goes one level down; null when nothing can. */
export function stepDown<C extends Levels>(specs: readonly ModuleSpec[], c: C): C | null {
  for (const m of byPriority(specs)) {
    const i = levelIndex(m, levelOf(m, c));
    if (i > levelIndex(m, m.minLevel)) return { ...c, [m.id]: m.levels[i - 1]! } as C;
  }
  return null;
}

/** The highest-priority module below its top goes one level up, if the memory budget allows it; null when nothing can. */
export function stepUp<C extends Levels>(specs: readonly ModuleSpec[], c: C, budgetMb: number, coreMb: number): C | null {
  for (const m of byPriority(specs).reverse()) {
    const i = levelIndex(m, levelOf(m, c));
    if (i < m.levels.length - 1) {
      const next = { ...c, [m.id]: m.levels[i + 1]! } as C;
      return memoryOf(specs, next, coreMb) <= budgetMb ? next : null;
    }
  }
  return null;
}
